import React, { useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { barIcon, tableIcon } from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import EChartCommon from '@op/components/EChartCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { formatFilterDate } from '@op/utils/time';
import { decimalFormatPercentage, reviewsFormat } from '@op/utils/value';
import { echartsContent, getPercentageInfo } from '@op/utils';
import './index.scss';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { DBO_ARR, DBO_Y } from '@op/utils/format';
import { formatTimeXAxisText } from '@op/utils/chart';
import { textShowFun } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/utils';
import { TableColumn } from 'tea-component';
import { downloadAction } from '@op/pages/download/utils';
import { dealWithOthersName } from '@op/utils/languageValue';
import steamPositive from '@op/assets/svg/steam_positive.svg';
import { NO_DATE } from '@op/constants';

interface ISingleLanguageChart {
  languageSelectOptions: Array<any>;
  filter: any;
  data: any;
  height?: number;
  selectedLanguage: string;
  setSelectedLanguage: (item: string) => void;
  isHourProps?: any;
}

interface ITable {
  filter: any;
  records: Array<any>;
  height?: number;
  type?: string;
}
export default function SingleLanguageChart(props: ISingleLanguageChart) {
  const {
    languageSelectOptions,
    selectedLanguage,
    setSelectedLanguage,
    filter,
    data,
    isHourProps,
  } = props;

  const [chartType, setChartType] = useState('bar');
  const { formatMessage } = useLanguage();
  const { tableRecord, ...timeline } = data || {};
  const { isEmpty } = data || {};
  return (
    <CardCommon
      operations={[barIcon, tableIcon]}
      defaultOperation={chartType}
      handleOperationsOnClick={(item) => {
        setChartType(item?.symbol || 'bar');
      }}
      className="dbo-single-language-card"
      hasDownload={!isEmpty}
      contentCard
      title={
        <div className="dbo-card-selects">
          {!isEmpty && (
            <SortBySelect
              defaultValue={selectedLanguage}
              onChange={(key: string, value: any) => {
                setSelectedLanguage(value);
              }}
              defaultParams={{
                options: languageSelectOptions,
                needFormatMessage: false,
                btnTextPrefix: formatMessage({ id: 'Language' }),
                matchButtonWidth: true,
                dealwithButton: (value: any, { textMap }: any) =>
                  textMap[value],
              }}
            />
          )}
        </div>
      }
      handleDownloadOnClick={() => {
        downloadAction({
          key: 'SingleSupportedLanguageReviews',
          fileName: `Single Supported Language - ${dealWithOthersName(
            selectedLanguage,
            'language',
          )}`,
          filter,
          auxiliaryParams: {},
          data: tableRecord,
        });
      }}
      loadingHeight={304}
      emptyHeight={304}
      empty={isEmpty}
      {...isHourProps}
    >
      {chartType === 'bar' ? (
        <BarChart {...props} data={timeline} />
      ) : (
        <Table {...props} records={tableRecord} />
      )}
    </CardCommon>
  );
}

const keyMapText: any = {
  all_reviews: {
    name: 'All Reviews',
    type: 'bar',
    label: { formatter: (val: number) => reviewsFormat(val) },
    yAxisIndex: 0,
  },
  positive: {
    name: 'All Reviews Positive%',
    type: 'line',
    label: {
      formatter: (params: { value: number }) => {
        const value = params?.value || 0;
        if (value) {
          return decimalFormatPercentage(value);
        }
        return '';
      },
    },
    yAxisIndex: 1,
  },
};
const BarChart = (props: any) => {
  const { data, height = 304, filter } = props;

  const { formatMessage } = useLanguage();
  const dateType = filter?.date_type || 'daily';

  const options = useMemo(() => {
    const { series: list, xAxis } = data || {};
    const legendInfo: Array<string> = [];

    const series = (list || []).map((item: any) => {
      const { name, data } = item || {};

      const itemInfo = keyMapText[name] || {};

      legendInfo.push(itemInfo.name);

      return { data, ...itemInfo };
    });

    const options = {
      grid: { top: 20 },
      xAxis: {
        type: 'category',
        data: xAxis || [],
        axisLabel: {
          formatter: (value: string) => formatTimeXAxisText(value, dateType),
        },
      },
      tooltip: {
        extraCssText: 'z-index:8',
        confine: true,
        trigger: 'axis',
        formatter: (params: any) => {
          const title = listTimeTitleFunc(params[0].axisValue, dateType);
          const textFormatFunc = textShowFun(formatMessage);
          return formatChartParamsDate({
            params,
            // isSort: true,
            beforeSort: false,
            valueFormat: (value: number, element) => {
              if (element.seriesName === 'All Reviews Positive%') {
                return decimalFormatPercentage(value, '-', 2);
              }
              return reviewsFormat(Math.abs(value), 2);
            },
            textShowFun: textFormatFunc,
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                if (!item.originValue) return null;
                const share = getPercentageInfo(
                  Math.abs(item.originValue),
                  total,
                  2,
                );
                return {
                  ...item,
                  share: share === '-' || share === '0%' ? '-' : share,
                };
              });
              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: `${title}`,
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: '',
                },
              ];

              return [
                {
                  records,
                  columns,
                },
              ];
            },
          });
        },
        // triggerOn: isDetail ? 'click' : 'mousemove',
        axisPointer: {
          type: 'shadow',
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
          },
          nameTextStyle: {
            color: '#444444',
            fontWeight: 400,
            fontSize: '12px',
          },
          splitNumber: 5,
        },
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => DBO_Y.percent(value, false),
            color: '#838697',
          },
          splitNumber: 5,
          min: 0,
          max: 1,
        },
      ],
      series,
      legend: {
        itemWidth: 15,
        formatter: (name: string) => {
          const newName = formatMessage({ id: name || NO_DATE.EMPTY });
          if (name.includes('Positive%')) {
            return `{positive|} ${newName}`;
          }
          return `${newName}`;
        },
        textStyle: {
          rich: {
            positive: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: steamPositive,
              },
            },
          },
        },
        data: legendInfo,
      },
    };

    return options;
  }, [data, dateType, filter, formatMessage]);

  return (
    <EChartCommon
      height={height}
      options={options}
      leftTitle={formatMessage({ id: 'All Reviews' })}
      rightTitle={formatMessage({ id: 'All Reviews Positive%' })}
      optionsProps={{
        type: 'bar',
      }}
    />
  );
};

const Table = (props: ITable) => {
  const { filter, records, height = 300 } = props;

  const { formatMessage } = useLanguage();

  const tableColumns = useMemo(() => {
    const columns: TableColumn[] = [
      {
        key: 'time',
        header: formatMessage({ id: 'Date' }),
        align: 'left',
        render: (cvm: any) => formatFilterDate(cvm.time, filter),
      },
      {
        key: 'all_reviews',
        header: formatMessage({ id: 'All Reviews' }),
        render: (cvm: any) => reviewsFormat(cvm.all_reviews),
        align: 'right',
      },
      {
        key: 'positive',
        header: formatMessage({ id: 'All Reviews Positive%' }),
        render: (cvm: any) =>
          getPercentageInfo(cvm.positive_reviews, cvm.all_reviews),
        align: 'right',
      },
    ];

    return columns;
  }, [filter, formatMessage]);

  const tableData = useMemo(() => {
    const table = [...(records || [])];
    table.sort(DBO_ARR.sortByTime('time', 'desc'));
    return table;
  }, [records]);

  return (
    <TableCommonHook
      recordKey={'key'}
      columns={tableColumns}
      total={records?.length || 0}
      records={tableData}
      hasPageable={false}
      maxHeight={height - 50}
      minWidth={'auto'}
      style={{ height }}
    />
  );
};
